import React from 'react';
import ConnectAccounts from '../ConnectAccounts';

// Resolve a company logo for a job card. Prefer a scraped logoUrl; otherwise
// guess the company's domain and pull its brand logo from Clearbit. If neither
// loads, the <img> onError handler reveals the company-initial fallback.
function guessDomain(name) {
  if (!name) return '';
  const clean = name.toLowerCase()
    .replace(/[.,]/g, ' ')
    .replace(/\b(inc|llc|ltd|corp|corporation|co|company|group|technologies|technology|solutions|consulting|systems|services|staffing|labs|the)\b/g, '')
    .replace(/[^a-z0-9]/g, '');
  return clean ? `${clean}.com` : '';
}
function jobLogoSrc(job) {
  if (job.logoUrl) return job.logoUrl;
  const d = guessDomain(job.company);
  return d ? `https://logo.clearbit.com/${d}` : '';
}
// Secondary source (Google favicon) tried if the primary logo 404s.
function jobLogoFallback(job) {
  const d = guessDomain(job.company);
  return d ? `https://www.google.com/s2/favicons?domain=${d}&sz=64` : '';
}

// Brand logo for the source platform badge on each job card.
const PLATFORM_DOMAINS = { linkedin: 'linkedin.com', indeed: 'indeed.com', dice: 'dice.com', monster: 'monster.com' };
function platformLogo(platform) {
  const d = PLATFORM_DOMAINS[(platform || '').toLowerCase()];
  return d ? { src: `https://logo.clearbit.com/${d}`, fb: `https://www.google.com/s2/favicons?domain=${d}&sz=64` } : null;
}

/**
 * Live Auto-Apply tab content. Browser-extension based — runs LinkedIn,
 * Indeed, Dice, Monster inside the user's logged-in browser session.
 *
 * Renders:
 *   - Section header
 *   - Live console (streaming logs from the extension)
 *   - "Applications This Session" cards grid
 *   - ConnectAccounts widget (when extension isn't installed)
 *   - Extension install banner
 *
 * Pure presentation. Parent owns all state; we receive everything as props.
 */
export default function LiveTab({
  // Console
  logs,
  isRunning,
  consoleBodyRef,
  consoleEndRef,

  // Applied jobs
  appliedJobs,

  // Extension presence + connect-accounts
  extInstalled,
  user,
  preferences,
  setCanStart,
  apiBaseUrl,
}) {
  return (
    <div key="tab-live" className="ajp-tab-pane">

      {/* Two-pane live area:
          LEFT  — streaming Live Application Console (extension logs)
          RIGHT — Applications This Session (cards of jobs being / already applied)
          Splits the right column horizontally so users can watch the log AND see
          the job results at the same time instead of scrolling between them. */}
      <div className="ajp-live-split">
        {/* Console */}
        <div className="ajp-console ajp-live-split-pane glass">
          <div className="ajp-console-header">
            <span>🖥️ Live Application Console</span>
          </div>
          <div className="ajp-console-body" ref={consoleBodyRef}>
            {logs.length === 0 && !isRunning && (
              <div className="ajp-empty" style={{ padding: '32px 16px' }}>
                <div className="ajp-empty-icon">⚡</div>
                <div className="ajp-empty-title">Ready to apply.</div>
                <div className="ajp-empty-sub">
                  Pick your platforms above, then click <strong>Launch Auto Apply</strong>. We'll stream every step right here as it happens.
                </div>
              </div>
            )}
            {logs.map((log, i) => (
              <div key={i} className={`ajp-log ajp-log-${log.type}`}>
                <span>{log.message}</span>
              </div>
            ))}
            <div ref={consoleEndRef} />
          </div>
        </div>

        {/* Applications This Session */}
        <div className="ajp-jobs-cards ajp-live-split-pane">
          <div className="ajp-jobs-cards-header">
            <h4 className="ajp-jobs-cards-title">Applications This Session</h4>
            <span className="ajp-jobs-count">{appliedJobs.length} jobs</span>
          </div>
          {appliedJobs.length === 0 ? (
            <div className="ajp-empty" style={{ padding: '32px 16px' }}>
              <div className="ajp-empty-icon">🗂️</div>
              <div className="ajp-empty-title">No applications yet.</div>
              <div className="ajp-empty-sub">
                Each job we apply to will appear here with its status (Applying, Applied, Skipped, Failed).
              </div>
            </div>
          ) : (
          <div className="ajp-cards-grid">
            {[...appliedJobs].reverse().map((job, i) => {
              const statusConfig = {
                applied:  { label: 'Applied',      icon: '✅', cls: 'applied'  },
                skipped:  { label: 'Skipped',      icon: '⏭️', cls: 'skipped'  },
                applying: { label: 'Applying...',  icon: '⏳', cls: 'applying' },
                failed:   { label: 'Failed',       icon: '❌', cls: 'failed'   },
              };
              // Unknown / transient statuses default to 'applying' (yellow),
              // NOT 'failed' (red). The previous default caused freshly
              // queued jobs to flash a red "Failed" badge before the real
              // status arrived from the extension.
              const s = statusConfig[job.status] || statusConfig.applying;
              const companyInitial = (job.company || job.platform || '?')[0].toUpperCase();
              const logoSrc = jobLogoSrc(job);
              const timeStr = job.appliedAt
                ? new Date(job.appliedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : '';
              return (
                <div key={i} className={`ajp-job-card ajp-job-card--${s.cls}`}>
                  <div className="ajp-job-card-logo">
                    {logoSrc
                      ? <img
                          src={logoSrc}
                          data-fb={jobLogoFallback(job)}
                          alt={job.company}
                          onError={e => {
                            const fb = e.currentTarget.getAttribute('data-fb');
                            if (fb && e.currentTarget.src !== fb) { e.currentTarget.removeAttribute('data-fb'); e.currentTarget.src = fb; }
                            else { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }
                          }}
                        />
                      : null}
                    <span style={{ display: logoSrc ? 'none' : 'flex' }}>{companyInitial}</span>
                  </div>

                  <div className="ajp-job-card-body">
                    <div className="ajp-job-card-top">
                      <span className="ajp-job-card-title">{job.title || '—'}</span>
                      {(() => {
                        const pl = platformLogo(job.platform);
                        return (
                          <span className="ajp-platform-tag" title={job.platform}>
                            {pl && (
                              <img
                                src={pl.src}
                                data-fb={pl.fb}
                                alt={job.platform}
                                width="24" height="24"
                                style={{ borderRadius: 5, display: 'inline-block', verticalAlign: 'middle', objectFit: 'contain' }}
                                onError={e => {
                                  const fb = e.currentTarget.getAttribute('data-fb');
                                  if (fb && e.currentTarget.src !== fb) { e.currentTarget.removeAttribute('data-fb'); e.currentTarget.src = fb; }
                                  else { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'inline'; }
                                }}
                              />
                            )}
                            <span style={{ display: pl ? 'none' : 'inline' }}>{job.platform}</span>
                          </span>
                        );
                      })()}
                    </div>
                    <div className="ajp-job-card-meta">
                      {job.company  && <span className="ajp-meta-item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>{job.company}</span>}
                      {job.location && <span className="ajp-meta-item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>{job.location}</span>}
                      {job.salary   && <span className="ajp-meta-item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>{job.salary}</span>}
                    </div>
                    <div className="ajp-job-card-footer">
                      <div className="ajp-job-card-chips">
                        {job.employmentType && <span className="ajp-meta-chip">{job.employmentType}</span>}
                        {job.postedDate && <span className="ajp-meta-item ajp-meta-muted">Posted {job.postedDate}</span>}
                        {timeStr && <span className="ajp-meta-item ajp-meta-muted"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>{timeStr}</span>}
                      </div>
                      <span className={`ajp-job-status-badge ajp-job-status--${s.cls}`}>{s.icon} {s.label}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </div>

      {/* Connect-accounts widget when no extension */}
      {!extInstalled && !isRunning && (
        <ConnectAccounts
          token={localStorage.getItem('token')}
          userId={user?.id}
          platforms={preferences.platforms}
          onReady={() => setCanStart(true)}
          apiBaseUrl={apiBaseUrl}
        />
      )}

      {!extInstalled && (
        <div className="ajp-ext-banner ajp-ext-banner--install">
          <span>🔌 Install the <strong>Terview Chrome Extension</strong> for undetectable automation — no bot blocks, no Puppeteer</span>
        </div>
      )}
    </div>
  );
}